/**
 * Utility functions for parsing various data formats
 */

/**
 * Parse description list from string to array
 * @param {string} descStr - Description string, potentially in JSON format
 * @returns {Array} Array of description items
 */
export const parseDescriptionList = (descStr) => {
  if (!descStr) return [];
  let cleaned = descStr;
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.slice(1, -1);
  }
  cleaned = cleaned.replace(/\\"/g, '"').replace(/"{2,}/g, '"');
  try {
    const arr = JSON.parse(cleaned);
    return Array.isArray(arr) ? arr : [descStr];
  } catch (e) {
    return [descStr];
  }
};

/**
 * Parse language list from string to array
 * @param {string|Array} langStr - Language string or array
 * @returns {Array} Array of languages
 */
export const parseLanguageList = (langStr) => {
  if (!langStr) return [];
  try {
    // Handle arrays already in string format
    if (typeof langStr === 'string' && langStr.startsWith('[') && langStr.endsWith(']')) {
      return JSON.parse(langStr);
    }
    // Handle comma-separated strings
    if (typeof langStr === 'string') {
      return langStr.split(',').map(lang => lang.trim());
    }
    // Handle array objects
    if (Array.isArray(langStr)) {
      return langStr;
    }
  } catch (e) {
    console.error("Error parsing languages:", e);
  }
  // Return original as single-item array if all else fails
  return [langStr];
};

/**
 * Parse dates for sorting, handling multiple formats
 * @param {string} dateStr - Date string in various formats
 * @returns {Date|null} JavaScript Date object or null if invalid
 */
export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  // Try different date formats
  const formats = [
    // MM/DD/YYYY
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
    // MM-DD-YYYY
    /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
    // YYYY-MM-DD
    /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
    // DD MMM YYYY
    /^(\d{1,2}) ([A-Za-z]{3}) (\d{4})$/,
  ];

  for (const format of formats) {
    const match = dateStr.match(format);
    if (match) {
      // Format specific parsing based on regex match
      if (format === formats[0] || format === formats[1]) {
        // MM/DD/YYYY or MM-DD-YYYY
        return new Date(match[3], match[1] - 1, match[2]);
      } else if (format === formats[2]) {
        // YYYY-MM-DD
        return new Date(match[1], match[2] - 1, match[3]);
      } else if (format === formats[3]) {
        // DD MMM YYYY
        const months = {
          Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
          Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
        };
        return new Date(match[3], months[match[2]], match[1]);
      }
    }
  }

  // Fallback to direct date parsing
  return new Date(dateStr);
};